import React, { useMemo } from 'react';
import type { Arrow } from '@/stores/arrowStore';
import type { Item } from '@/stores/canvasStore';
import { arrowPaint, computeArrowGeometry, type ArrowSiblingInfo } from './arrowGeometry';

interface ArrowPathProps {
  arrow: Arrow;
  items: Item[];
  isSelected: boolean;
  onClick: () => void;
  siblingInfo?: ArrowSiblingInfo;
}

export function ArrowPath({ arrow, items, isSelected, onClick, siblingInfo }: ArrowPathProps) {
  // Extract connected items outside useMemo so their positions can be explicit dependencies
  const startItem = arrow.start_item_id ? items.find(i => i.id === arrow.start_item_id) : null;
  const endItem = arrow.end_item_id ? items.find(i => i.id === arrow.end_item_id) : null;

  const pathData = useMemo(
    () => computeArrowGeometry(arrow, startItem, endItem, siblingInfo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arrow, startItem?.position_x, startItem?.position_y, startItem?.width, startItem?.height, endItem?.position_x, endItem?.position_y, endItem?.width, endItem?.height, siblingInfo]
  );

  if (!pathData) return null;

  const { start, end, path, startAngle, endAngle, midpoint } = pathData;
  const paint = arrowPaint(arrow.stroke_color);
  const strokeWidth = arrow.stroke_width ?? 2;
  const isAnimated = arrow.animated ?? false;
  // Head grows a little with the line so thick arrows don't get a pin-sized tip
  const head = 4 + strokeWidth * 2;
  const headPoints = `${-head},${-head * 0.6} 0,0 ${-head},${head * 0.6}`;
  // Labels: 12px Inter, roughly 6.6px a character, padded into a pill
  const labelWidth = arrow.label ? Math.ceil(arrow.label.length * 6.6) + 16 : 0;

  return (
    <g className="arrow-path" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Invisible wider path for easier clicking */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: 'pointer' }}
      />

      {/* Selection: a soft signal band under the line; the line keeps its own colour */}
      {isSelected && (
        <path
          d={path}
          fill="none"
          strokeWidth={strokeWidth + 8}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ stroke: 'hsl(var(--signal))', opacity: 0.22 }}
        />
      )}

      {/* Visible path */}
      <path
        d={path}
        fill="none"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isAnimated ? '8 4' : 'none'}
        className={isAnimated ? 'animate-dash' : ''}
        style={{ stroke: paint }}
      />

      {/* Start arrowhead */}
      {arrow.start_arrowhead && (
        <polygon
          points={headPoints}
          strokeWidth={1}
          strokeLinejoin="round"
          style={{ fill: paint, stroke: paint }}
          transform={`translate(${start.x}, ${start.y}) rotate(${startAngle})`}
        />
      )}

      {/* End arrowhead */}
      {arrow.end_arrowhead && (
        <polygon
          points={headPoints}
          strokeWidth={1}
          strokeLinejoin="round"
          style={{ fill: paint, stroke: paint }}
          transform={`translate(${end.x}, ${end.y}) rotate(${endAngle})`}
        />
      )}

      {/* Label: a paper pill sitting on the line */}
      {arrow.label && (
        <g className="select-none">
          <rect
            x={midpoint.x - labelWidth / 2}
            y={midpoint.y - 11}
            width={labelWidth}
            height={22}
            rx={11}
            strokeWidth={1}
            style={{ fill: 'hsl(var(--paper-raised))', stroke: 'hsl(var(--line))' }}
          />
          <text
            x={midpoint.x}
            y={midpoint.y}
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: '12px', fontWeight: 500, fill: 'hsl(var(--ink-2))' }}
          >
            {arrow.label}
          </text>
        </g>
      )}

      {/* Selection handles */}
      {isSelected && (
        <>
          {[start, end].map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={5}
              strokeWidth={1.5}
              className="cursor-move"
              style={{ fill: 'hsl(var(--paper-raised))', stroke: 'hsl(var(--signal))' }}
            />
          ))}
        </>
      )}
    </g>
  );
}
